import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";
import it from "./locales/it.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import pt from "./locales/pt.json";
import { mergeParts } from "./locales/parts/index";
import { home } from "./locales/parts/home";
import { studio } from "./locales/parts/studio";
import { planner } from "./locales/parts/planner";
import { quiz } from "./locales/parts/quiz";
import { flashcards } from "./locales/parts/flashcards";
import { stats } from "./locales/parts/stats";
import { learn } from "./locales/parts/learn";
import { tools } from "./locales/parts/tools";
import { auth } from "./locales/parts/auth";
import { onboarding } from "./locales/parts/onboarding";
import { badges } from "./locales/parts/badges";
import { programs } from "./locales/parts/programs";
import { prep } from "./locales/parts/prep";
import { breaks } from "./locales/parts/breaks";
import { widgets } from "./locales/parts/widgets";
import { voice } from "./locales/parts/voice";
import { video } from "./locales/parts/video";
import { notes } from "./locales/parts/notes";
import { visuals } from "./locales/parts/visuals";
import { library } from "./locales/parts/library";
import { states } from "./locales/parts/states";
import { lumen } from "./locales/parts/lumen";
import { community } from "./locales/parts/community";
import { gamify } from "./locales/parts/gamify";
import { rewards } from "./locales/parts/rewards";
import { notetaking } from "./locales/parts/notetaking";
import { studybreaks } from "./locales/parts/studybreaks";
import { teacher } from "./locales/parts/teacher";
import { assess } from "./locales/parts/assess";
import { analytics } from "./locales/parts/analytics";
import { report } from "./locales/parts/report";
import { review } from "./locales/parts/review";
import { progress } from "./locales/parts/progress";
import { recovery } from "./locales/parts/recovery";
import { roleui } from "./locales/parts/roleui";
import { explore, todayFocus } from "./locales/parts/explore";

const bundles = mergeParts(
  { en, it, es, fr, de, pt },
  {
    home,
    studio,
    planner,
    quiz,
    flashcards,
    stats,
    learn,
    tools,
    auth,
    onboarding,
    badges,
    programs,
    prep,
    breaks,
    widgets,
    voice,
    video,
    notes,
    visuals,
    library,
    states,
    lumen,
    community,
    gamify,
    rewards,
    notetaking,
    studybreaks,
    teacher,
    assess,
    analytics,
    report,
    review,
    progress,
    recovery,
    roleui,
    explore,
    today: todayFocus,
  },
);

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English", flag: "🇬🇧" },
  { code: "it", name: "Italian", nativeName: "Italiano", flag: "🇮🇹" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
  { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
  { code: "de", name: "German", nativeName: "Deutsch", flag: "🇩🇪" },
  { code: "pt", name: "Portuguese", nativeName: "Português", flag: "🇵🇹" },
] as const;

export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const DEFAULT_LANGUAGE: AppLanguage = "en";

export function languageNameFromCode(code: string): string {
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.name ?? "English";
}

export function normalizeLanguage(input: string | null | undefined): AppLanguage {
  if (!input) return DEFAULT_LANGUAGE;
  const base = input.toLowerCase().split(/[-_]/)[0];
  const found = SUPPORTED_LANGUAGES.find((l) => l.code === base);
  return (found?.code ?? DEFAULT_LANGUAGE) as AppLanguage;
}

let initialized = false;
export function initI18n() {
  if (initialized) return i18n;
  initialized = true;
  i18n.use(initReactI18next).init({
    resources: Object.fromEntries(
      Object.entries(bundles).map(([lang, translation]) => [lang, { translation }]),
    ),
    // Always start from the same language on server and client so SSR markup
    // matches hydration; the stored/profile language is applied in an effect.
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    load: "languageOnly",
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
  return i18n;
}

export default i18n;
export const LANGUAGE_STORAGE_KEY = "lumen.lang";

/** Language stored by the user, or the browser preference. Client-only. */
export function detectStoredLanguage(): AppLanguage {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) return normalizeLanguage(stored);
  } catch {
    /* storage blocked */
  }
  return normalizeLanguage(window.navigator.language);
}

export function persistLanguage(code: AppLanguage) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    /* storage blocked */
  }
}
